/**
 * @fileoverview Regras puras dos textos das abordagens inteligentes: o horário
 * dos envios automáticos, "daqui a quanto tempo" sai a próxima mensagem e o que
 * a faixa da conversa mostra de uma abordagem (fase, tom e texto). `t` traduz
 * as chaves de `smartApproaches.*`. Testadas em
 * __tests__/smartApproachText.test.js.
 */

const WEEK = [1, 2, 3, 4, 5, 6, 0];
const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;
// A varredura põe na fila a cada minuto: passou disso, o envio está parado (fila ou envios desligados).
const LATE_MS = 10 * MINUTE_MS;

/** Abordagem encerrada continua na faixa por 24 h. */
export const ENDED_VISIBLE_MS = DAY_MS;

/**
 * Horário de envio em texto curto: "seg., ter., qua. · 08:00–20:00", ou "seg. a
 * sáb." quando os dias são seguidos.
 */
export const windowText = (window, t) => {
  if (!window || !window.days || !window.days.length) return "";
  const dayName = (d) => t(`days.d${d}`);
  const ordered = WEEK.filter((d) => window.days.includes(d));
  const positions = ordered.map((d) => WEEK.indexOf(d));
  const consecutive = positions.every((p, i) => i === 0 || p === positions[i - 1] + 1);
  const days =
    ordered.length > 2 && consecutive
      ? t("days.range", { from: dayName(ordered[0]), to: dayName(ordered[ordered.length - 1]) })
      : ordered.map(dayName).join(", ");
  return `${days} · ${window.start}–${window.end}`;
};

/** "2ª", "3ª"... */
export const ordinal = (n, t) => t(`ordinals.n${n}`);

/** "25 min", "4 h", "3 h 40 min" (arredonda para cima: nunca promete antes da hora). */
export const durationText = (ms, t) => {
  const total = Math.max(1, Math.ceil(ms / MINUTE_MS));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (!hours) return t("duration.minutes", { minutes });
  if (!minutes) return t("duration.hours", { hours });
  return t("duration.hoursMinutes", { hours, minutes });
};

/**
 * Quando sai um envio: "em instantes", "em 3 h 40 min" ou, a partir de 24 h,
 * "em 21/10 às 08:00". Mais de 10 min depois da hora, "com atraso" (não promete
 * o que está parado). `formatAt(date)` dá `{ date, time }` no idioma da tela.
 */
export const whenText = (target, now, t, formatAt) => {
  const at = new Date(target);
  const ms = at.getTime() - now;
  if (ms < -LATE_MS) return t("when.late");
  if (ms <= MINUTE_MS) return t("when.soon");
  if (ms < DAY_MS) return t("when.in", { duration: durationText(ms, t) });
  return t("when.at", formatAt(at));
};

const time = (value) => (value ? new Date(value).getTime() : 0);

/** Quando a abordagem acabou para a faixa: o fim, a resposta ou o fim da janela sem resposta. */
const endedAt = (run) =>
  Math.max(time(run.finishedAt), time(run.firstReplyAt), run.outcome === "no_reply" ? time(run.replyDeadlineAt) : 0);

const FAILED_STEP_STATUSES = ["failed", "uncertain", "expired"];

/** A mensagem em que a abordagem parou com erro. */
const failedStep = (run) => {
  const bad = [...(run.steps || [])].reverse().find((s) => FAILED_STEP_STATUSES.includes(s.status));
  return bad?.step || run.nextStep || run.lastSentStep || 1;
};

const CANCEL_REASONS = ["manual", "agent_message", "ticket_closed", "flow_disabled", "opt_out", "expired"];
const FAIL_REASONS = [
  "template_unavailable",
  "invalid_variables",
  "no_phone",
  "send_failed",
  "send_uncertain",
  "delivery_failed",
  "start_interrupted",
];

/** Por que parou, em texto curto ("por Maria", "atendimento encerrado"...). */
const stopText = (run, t) => {
  const step = ordinal(failedStep(run), t);
  if (run.status === "cancelled") {
    const reason = CANCEL_REASONS.includes(run.stopReason) ? run.stopReason : "other";
    if ((reason === "manual" || reason === "agent_message") && run.stoppedBy) {
      return t(`bar.cancelled.${reason}By`, { name: run.stoppedBy });
    }
    return t(`bar.cancelled.${reason}`, { step });
  }
  const reason = FAIL_REASONS.includes(run.stopReason) ? run.stopReason : "other";
  return t(`bar.failed.${reason}`, { step });
};

/**
 * O que a faixa da conversa mostra (null: nada). `phase`: "pending" (há envio
 * por fazer: dá para cancelar), "waiting" (tudo enviado, à espera da resposta)
 * ou "ended" (por 24 h). `tone`: neutral, ok (o cliente respondeu) ou bad (erro).
 */
export const runView = (run, { now, t, formatAt }) => {
  if (!run) return null;
  if (run.status === "active") {
    const progress = run.lastSentStep > 1 ? t("bar.sent", { step: ordinal(run.lastSentStep, t) }) : t("bar.awaiting");
    const next =
      run.nextStep && run.nextSendAt
        ? t("bar.next", { step: ordinal(run.nextStep, t), when: whenText(run.nextSendAt, now, t, formatAt) })
        : null;
    return { phase: "pending", tone: "neutral", title: t("bar.active"), detail: [progress, next].filter(Boolean).join(" · ") };
  }
  if (run.status === "completed" && !run.outcome) {
    return { phase: "waiting", tone: "neutral", title: t("bar.title"), detail: t("bar.allSent") };
  }
  if (now - endedAt(run) >= ENDED_VISIBLE_MS) return null;

  const replyStep = ordinal(run.repliedAfterStep || run.lastSentStep || 1, t);
  if (run.status === "replied") {
    return { phase: "ended", tone: "ok", title: t("bar.interrupted"), detail: t("bar.replied", { step: replyStep }) };
  }
  if (run.status === "completed") {
    if (run.outcome === "replied") {
      return { phase: "ended", tone: "ok", title: t("bar.completed"), detail: t("bar.replied", { step: replyStep }) };
    }
    if (run.outcome === "void") return { phase: "ended", tone: "bad", title: t("bar.completed"), detail: t("bar.void") };
    return { phase: "ended", tone: "neutral", title: t("bar.completed"), detail: t("bar.noReply") };
  }
  if (run.status === "cancelled") {
    return {
      phase: "ended",
      tone: run.stopReason === "expired" ? "bad" : "neutral",
      title: t("bar.cancelledTitle"),
      detail: stopText(run, t),
    };
  }
  return { phase: "ended", tone: "bad", title: t("bar.interrupted"), detail: stopText(run, t) };
};

/**
 * Situação de cada mensagem na linha do tempo "Detalhes": as que já existem
 * (com a entrega da Meta) e as previstas que ainda não foram agendadas.
 * `tone` segue a faixa: ok (entregue/lida), bad (falhou), neutral.
 */
export const stepLines = (run, { now, t, formatAt }) => {
  const byStep = new Map((run.steps || []).map((s) => [s.step, s]));
  const total = run.totalMessages || 1;
  const lines = [];
  for (let n = 1; n <= total; n += 1) {
    const step = byStep.get(n);
    const label = t("details.step", { step: ordinal(n, t) });
    if (!step) {
      // Ainda não agendada: a anterior não saiu. Com a abordagem parada, não sai mais.
      const planned = (run.plannedSteps || []).find((p) => p.step === n);
      lines.push({
        step: n,
        label,
        tone: "neutral",
        status:
          run.status === "active" && planned
            ? t("details.planned", { hours: planned.delayHours })
            : t("details.status.cancelled"),
        at: null,
      });
      continue;
    }
    if (step.status === "sent") {
      const { ack } = step;
      const status = ack === -1 ? "failed" : ack >= 3 ? "read" : ack === 2 ? "delivered" : "sent";
      lines.push({
        step: n,
        label,
        tone: status === "failed" ? "bad" : status === "sent" ? "neutral" : "ok",
        status: t(`details.status.${status}`),
        at: step.sentAt,
        templateName: step.templateName,
      });
      continue;
    }
    if (step.status === "scheduled" || step.status === "sending") {
      lines.push({
        step: n,
        label,
        tone: "neutral",
        status:
          step.status === "sending"
            ? t("details.status.sending")
            : t("details.scheduled", { when: whenText(step.dueAt, now, t, formatAt) }),
        at: null,
        templateName: step.templateName,
      });
      continue;
    }
    lines.push({
      step: n,
      label,
      tone: step.status === "cancelled" ? "neutral" : "bad",
      status: t(`details.status.${step.status}`),
      at: null,
      templateName: step.templateName,
      error: step.errorMessage || null,
    });
  }
  return lines;
};
